
import { Component, Prop, Vue } from 'vue-property-decorator';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../../framework/state';
import {
    deleteResource,
    getResourcePagingNew,
    getResourcesNew,
    putResource,
} from '../../../../../framework/client/resource';
import { formatCurrency, formatToFinnishDate } from '../../../../service/helper_service';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { PartPaymentRow } from '../../../../../../common/application/model/PartPaymentRow';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';
import { errorToast, successToast } from '../../../../service/toast_service';
import ParameterService from '../../../../service/parameter_service';
import { DateTime } from 'luxon';
import NumberField from '../../../../../framework/fields/NumberField.vue';
import {
    createPartPaymentRows,
    deletePartPaymentRows,
    generatePartPaymentFromFile,
    updatePartPaymentRow,
} from '../../../../../../browser/application/client/part_payment_client';
import AddDocument from '../../document/AddDocument.vue';
import { FileMeta } from '../../../../../../common/application/model/FileMeta';
import { Document } from '../../../../../../common/application/model/Document';
import { v4 as uuid } from 'uuid';
import { GeneratePartPaymentRowsReq } from '../../../../../../common/application/model/api/GeneratePartPaymentRowsReq';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
        NumberField,
        AddDocument,
    },
})
export default class PartPaymentPanel extends Vue {
    // Properties
    @Prop(String) readonly vehicleContractId!: string;
    @Prop(String) readonly customerId!: string;
    @Prop(String) readonly contractStartDate!: string;
    @Prop(Number) readonly numberOfPeriods!: number;

    readonly resourceType = ApplicationResource.PART_PAYMENT_ROW;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as PartPaymentRow[],
        editingRow: {} as PartPaymentRow,
        total: 0,
        loading: false,
        selected: undefined as PartPaymentRow | undefined,
        parameters: this.parameterService.get(),
        hover: '',
        showAddButton: false,
        showDeleteButton: false,
        showGenerateButton: false,
        isGenerateRowsModalActive: false,
        droppedDocument: null as Document | null,
        uploading: false,
        newUploadingDocument: { public: false } as FileMeta,
        uploadedFileUniqueName: '',
    };

    async mounted() {
        this.local.loading = true;

        await this.loadAsyncData();

        this.local.loading = false;
    }

    async loadAsyncData() {
        this.local.loading = true;
        this.local.parameters.pageSize = 12;
        const partPaymentParameters = new Map<string, string>();
        partPaymentParameters.set('vehicleContractId', this.vehicleContractId);

        const existingRows = await getResourcesNew<PartPaymentRow>(
            this.resourceType,
            this.local.parameters.page,
            partPaymentParameters,
        );

        if (existingRows) {
            this.local.rows = existingRows;
        }
        const paging = await getResourcePagingNew(this.resourceType, partPaymentParameters);
        this.local.total = paging.rowCount;

        this.local.total === 0 ? this.rowsExisist(false) : this.rowsExisist(true);

        this.local.loading = false;
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    onRowClick(row: PartPaymentRow) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    formatDate(date: string): string {
        if (!date) {
            return '-';
        }
        return formatToFinnishDate(date);
    }

    editRow(row: PartPaymentRow) {
        this.local.editingRow = row;
    }

    async saveInlineEditedRow() {
        this.local.loading = true;
        try {
            await updatePartPaymentRow(this.local.editingRow);
            this.local.editingRow = {} as PartPaymentRow;
            await this.loadAsyncData();
            successToast(this, 'message.paymentRowsSaved');
        } catch (error) {
            errorToast(this);
        }
        this.local.loading = false;
    }

    cancel() {
        this.local.editingRow = {} as PartPaymentRow;
    }

    async addPaymentRows() {
        this.local.loading = true;
        const periodRows: PartPaymentRow[] = [];
        const startDate = this.contractStartDate;

        for (let i = 0; i < this.numberOfPeriods; i++) {
            const periodRow: PartPaymentRow = {
                id: '',
                vehicleContractId: this.vehicleContractId,
                rowIndex: i + 1,
                paymentPeriodStart: DateTime.fromFormat(startDate, 'yyyy-MM-dd').plus({ months: i }).toJSDate(),
                paymentPeriodEnd: DateTime.fromFormat(startDate, 'yyyy-MM-dd').plus({ months: i }).toJSDate(),
                interest: 0,
                interestRate: 0,
                partPayment: 0,
                otherFees: 0,
                remainingCapital: 0,
                created: new Date(),
                modified: new Date(),
            };

            periodRows.push(periodRow);
        }

        await createPartPaymentRows(periodRows).then((result) => {
            this.local.rows = result;
        });

        if (this.local.rows.length) {
            this.rowsExisist(true);
            this.local.total = this.local.rows.length;
        }

        this.local.loading = false;
    }

    async showGeneratePaymentRowsModal() {
        this.local.isGenerateRowsModalActive = true;
    }

    closeUploadModal() {
        this.local.isGenerateRowsModalActive = false;
    }

    async deletePaymentRows() {
        this.local.loading = true;
        await deletePartPaymentRows(this.vehicleContractId).then((deleted) => {
            if (deleted) {
                this.local.rows = [];
                this.rowsExisist(false);
                this.local.total = this.local.rows.length;
            }
        });
        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    rowsExisist(existingRows: boolean) {
        if (existingRows) {
            this.local.showAddButton = false;
            this.local.showGenerateButton = false;
            this.local.showDeleteButton = true;
        } else {
            this.local.showAddButton = true;
            this.local.showGenerateButton = true;
            this.local.showDeleteButton = false;
        }
    }

    async add() {
        try {
            if (!this.local.droppedDocument) {
                return;
            } else {
                const document: Document = this.local.droppedDocument;
                const documentType = document.name.substring(document.name.lastIndexOf('.') + 1);

                this.local.newUploadingDocument = {
                    ...this.local.newUploadingDocument,
                    id: uuid(),
                    created: new Date(),
                    modified: new Date(),
                    name: document.name,
                    mimeType: document.type,
                    fileExtension: documentType,
                    size: document.size,
                    category: ApplicationResource.PART_PAYMENT_ROW,
                    description: document.description || '',
                    resourceId: this.vehicleContractId,
                    resourceType: ApplicationResource.VEHICLE_CONTRACT,
                    customerId: this.customerId ? this.customerId : null,
                    userId: this.shared.context.userId,
                };

                await this.upload(this.local.newUploadingDocument);
            }
        } catch (error) {
            console.warn(error);
            errorToast(this);
        }
    }
    async upload(uploadedDocument: FileMeta) {
        this.local.uploading = true;

        try {
            if (this.local.droppedDocument) {
                const formData = new FormData();
                let file: any = this.local.droppedDocument;
                let name = file.name;
                file = new File([file], name, { type: file.type });

                formData.append('fileBinary', file);
                formData.append('fileId', uploadedDocument.id);
                formData.append('fileName', name);

                const xhr = new XMLHttpRequest();

                xhr.addEventListener('loadend', () => {
                    this.local.uploading = false;
                });

                xhr.addEventListener('error', () => {
                    errorToast(this);
                    this.local.uploading = false;
                });

                xhr.addEventListener('abort', () => {
                    this.local.uploading = false;
                });

                xhr.onreadystatechange = async () => {
                    console.log('xhr.status', xhr.status);
                    console.log('xhr.readyState', xhr.readyState);
                    if (xhr.readyState === 4) {
                        if (xhr.status !== 200) {
                            console.warn('Upload returned error status: ' + xhr.status);
                            errorToast(this);
                        }

                        if (xhr.status === 200) {
                            this.local.uploadedFileUniqueName = `${this.local.newUploadingDocument.id}_${this.local.newUploadingDocument.name}`;
                            const request: GeneratePartPaymentRowsReq = {
                                fileName: this.local.uploadedFileUniqueName,
                                vehicleContractId: this.vehicleContractId,
                            };
                            this.closeUploadModal();
                            this.local.loading = true;
                            await generatePartPaymentFromFile(request).then((result) => (this.local.rows = result));

                            if (this.local.rows.length) {
                                this.rowsExisist(true);
                                this.local.total = this.local.rows.length;
                            }

                            await this.loadAsyncData();
                            this.local.loading = false;
                        }
                    }
                };

                xhr.open(
                    'POST',
                    `/api/file/resource/${this.resourceType}/category/${uploadedDocument.category}?path=textract-documents/part-payment`,
                    true,
                );
                xhr.setRequestHeader('Authorization', 'Bearer ' + sharedState.context.idToken);
                xhr.send(formData);
            }
        } catch (error) {
            console.warn(error);
            errorToast(this);
            this.local.uploading = false;
        }
    }
}
